import * as React from "react";
import { MuseumObject, SimilarArtifact } from "@/backend/types/domain";
import { fixProxiedImageUrl, MuseumObjectCard } from "./MuseumObjectCard";
import {
  setArtifactViewSource,
  trackedFetch,
  trackEvent,
} from "../lib/analytics";

interface SimilarArtifactsProps {
  artifactId: string;
  onSelectArtifact?: (artifact: MuseumObject) => void;
  enabled?: boolean;
}

interface SimilarArtifactResponse {
  sourceId: string;
  type: string;
  count: number;
  artifacts: Array<MuseumObject & { similarity: number; matchType: string }>;
}

export const SimilarArtifacts: React.FC<SimilarArtifactsProps> = ({
  artifactId,
  onSelectArtifact,
  enabled = false,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [similarArtifacts, setSimilarArtifacts] = React.useState<
    SimilarArtifactResponse["artifacts"]
  >([]);
  const [similarityType, setSimilarityType] = React.useState<
    "semantic" | "visual" | "both"
  >("semantic");

  const fetchSimilarArtifacts = React.useCallback(async () => {
    if (!artifactId) return;

    setIsLoading(true);
    setError(null);
    const startedAt = performance.now();

    try {
      const apiBase =
        typeof window !== "undefined" &&
        window.location.hostname === "localhost"
          ? "http://localhost:3000"
          : "";
      const response = await trackedFetch(
        "similar",
        `${apiBase}/api/artifact/${artifactId}/similar?type=${similarityType}&limit=6`,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }

      const data: SimilarArtifactResponse = await response.json();
      setSimilarArtifacts(data.artifacts);
      trackEvent("similar_loaded", {
        result_count: data.artifacts.length,
        duration_ms: Math.round(performance.now() - startedAt),
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load similar artifacts",
      );
      setSimilarArtifacts([]);
    } finally {
      setIsLoading(false);
    }
  }, [artifactId, similarityType]);

  React.useEffect(() => {
    if (isExpanded) {
      fetchSimilarArtifacts();
    }
  }, [similarityType, isExpanded]);

  // Close similar artifacts panel when artifact changes
  React.useEffect(() => {
    setIsExpanded(false);
  }, [artifactId]);

  if (!enabled) {
    return null;
  }

  return (
    <div className="border-t-2 border-dashed border-theme-color pt-3 mt-3">
      <button
        onClick={() => {
          if (!isExpanded) trackEvent("similar_open");
          setIsExpanded(!isExpanded);
        }}
        className="flex items-center gap-2 w-full text-left font-medium uppercase text-sm hover:opacity-80"
      >
        <span
          className="transform transition-transform duration-200"
          style={{
            display: "inline-block",
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
          }}
        >
          ▶
        </span>
        Similar Artifacts
        {similarArtifacts.length > 0 && (
          <span className="text-gray-500">({similarArtifacts.length})</span>
        )}
      </button>

      {isExpanded && (
        <div className="mt-3">
          {/*  TODO: enable once we get visual similarity working
          
          <div className="flex gap-2 mb-3">
            {(["semantic", "visual", "both"] as const).map((type) => (
              <button
                key={type}
                onClick={() => setSimilarityType(type)}
                className={`px-3 py-1 text-xs uppercase border border-theme-color ${
                  similarityType === type
                    ? "bg-theme-color font-bold"
                    : "text-theme-color hover:bg-theme-color hover:font-bold"
                }`}
              >
                {type}
              </button>
            ))}
          </div> */}

          {isLoading && (
            <div className="text-center py-4 text-gray-500 animate-pulse">
              LOADING SIMILAR ARTIFACTS...
            </div>
          )}

          {error && (
            <div className="text-center py-4 text-red-500">{error}</div>
          )}

          {!isLoading && !error && similarArtifacts.length === 0 && (
            <div className="text-center py-4 text-gray-500">
              No similar artifacts found
            </div>
          )}

          {!isLoading && !error && similarArtifacts.length > 0 && (
            <div className="su-card-grid su-card-grid--standard">
              {similarArtifacts.map((artifact) => (
                <MuseumObjectCard
                  key={artifact.id}
                  obj={artifact}
                  showDetail={(obj) => {
                    trackEvent("similar_click");
                    setArtifactViewSource("similar");
                    if (onSelectArtifact) {
                      onSelectArtifact(obj);
                    } else {
                      window.location.hash = `artifact=${obj.id}`;
                      const modalTitle =
                        document.getElementById("modal-title");
                      modalTitle?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                      setIsExpanded(false);
                    }
                  }}
                  viewMode="standard"
                  badge={
                    <>
                      {Math.round(artifact.similarity * 100)}% ·{" "}
                      {artifact.matchType}
                    </>
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
